import { makeRequest } from './https';
import { AddInvoice, InvoiceEnteredResponse } from '../types/invoice.type';

export const addInvoice = (data: AddInvoice) => {
  return makeRequest({
    url: '/invoice',
    method: 'post',
    data,
  });
};

export const getInvoices = (organizationId: string, params?: any) => {
  return makeRequest({
    url: '/invoice',
    method: 'get',
    params: { 
      organization_id: organizationId, 
      parent_id: params?.parent_id, // NEW: support folder navigation
      type: params?.type, // NEW: support filtering by type
      search: params?.search, // NEW: support search
      page: params?.page || 1, // NEW: pagination support
      limit: params?.limit || 50, // NEW: pagination support (increased from 10)
      ...params 
    },
  });
};

export const getInvoice = (invoiceId: string) => {
  return makeRequest({
    url: `/invoice/${invoiceId}`,
    method: 'get',
  });
};

// NEW: Move invoice to different folder
export const moveInvoice = (invoiceId: string, targetFolderId: string) => {
  return makeRequest({
    url: `/invoice/${invoiceId}`,
    method: 'put',
    data: {
      parent_id: targetFolderId
    },
  });
}; 

// NEW: Bulk upload invoices
export const addBulkInvoices = (data: Array<AddInvoice>) => {
  return makeRequest({
    url: '/invoice/bulk',
    method: 'post',
    data,
  });
}; 

// NEW: Delete invoice/folder
export const deleteInvoice = (invoiceId: string) => {
  console.log('Delete service called with invoiceId:', invoiceId);
  return makeRequest({
    url: `/invoice/${invoiceId}`,
    method: 'delete',
  }).then(response => {
    console.log('Delete service response:', response);
    return response;
  }).catch(error => {
    console.error('Delete service error:', error);
    throw error;
  });
}; 

// NEW: Update invoice access level
export const updateInvoiceAccessLevel = (invoiceId: string, accessLevel: 'public' | 'private') => {
  return makeRequest({
    url: `/invoice/${invoiceId}`,
    method: 'put',
    data: {
      access_level: accessLevel
    },
  });
}; 

// NEW: Start OCR processing for invoice
export const processInvoiceOCR = (invoiceId: string) => {
  return makeRequest({
    url: `/invoice/${invoiceId}/process-ocr`,
    method: 'post',
  });
};

export const MAX_BATCH_OCR_SELECTION = 200;

/** Fetch file IDs in a folder for batch selection (capped at maxCount). */
export const setInvoiceEntered = (
  invoiceId: string,
  entered: boolean,
): Promise<InvoiceEnteredResponse> => {
  return makeRequest({
    url: `/invoice/${invoiceId}/entered`,
    method: 'patch',
    data: { entered },
  });
};

export const getFolderFileIds = async (
  organizationId: string,
  parentId?: string,
  maxCount = MAX_BATCH_OCR_SELECTION,
  entered?: boolean,
): Promise<{ ids: string[]; totalFiles: number; truncated: boolean }> => {
  const ids: string[] = [];
  let page = 1;
  const limit = 50;
  let totalFiles = 0;

  while (ids.length < maxCount) {
    const response = await getInvoices(organizationId, {
      parent_id: parentId,
      type: 'file',
      page,
      limit,
      ...(entered !== undefined ? { entered } : {}),
    });

    const items = response?.result_list || [];
    totalFiles = response?.total_count ?? items.length;

    for (const item of items) {
      if (item.type === 'file' && ids.length < maxCount) {
        ids.push(item.id);
      }
    }

    if (page * limit >= totalFiles || items.length === 0) {
      break;
    }
    page += 1;
  }

  return {
    ids,
    totalFiles,
    truncated: totalFiles > maxCount,
  };
};

// Batch OCR — start extraction for multiple invoices (max 200)
export const batchProcessInvoiceOCR = (invoiceIds: string[]) => {
  return makeRequest({
    url: '/invoice/batch-process-ocr',
    method: 'post',
    data: { invoice_ids: invoiceIds },
  });
};

export interface BatchOCRStatusData {
  total: number;
  completed: number;
  failed: number;
  processing: number;
  progress: number; // 0-100
}

// Poll aggregate OCR progress for a set of invoices
export const getBatchOCRStatus = (invoiceIds: string[]): Promise<BatchOCRStatusData> => {
  return makeRequest({
    url: '/invoice/batch-ocr-status',
    method: 'get',
    params: { invoice_ids: invoiceIds.join(',') },
  });
}; 